import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgModule,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ImageMedia } from '@icilalune/types';
import { from, Observable, Subscription, tap } from 'rxjs';
import Player, { Options } from '@vimeo/player';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageVisibilityService } from '@icilalune/utils';
import { RxEffects } from '@rx-angular/state/effects';
import { VimeoMedia } from '@nx-icilalune/drupal-types';
import { ComponentStore } from '@ngrx/component-store';
import {
  MUTE_VIMEO_VIDEO,
  VimeoPauseEvent,
  VimeoPlayEvent,
} from '@nx-icilalune/icilalune/media/tokens';

interface MediaVimeoState {
  mute: boolean;
}

@UntilDestroy()
@Component({
  selector: 'i2l-media-vimeo',
  template: ` <div #vimeoContainer></div> `,
  styleUrls: ['./media-vimeo.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [RxEffects],
})
export class MediaVimeoComponent
  extends ComponentStore<MediaVimeoState>
  implements AfterViewInit
{
  @Input()
  video!: VimeoMedia;

  @Input()
  thumb!: ImageMedia;

  @Output()
  isPlaying: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  currentTime: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  currentRatio: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  duration: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  buffering: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  stopped: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('vimeoContainer') vimeoContainer!: ElementRef<HTMLDivElement>;
  #subscriptions: Subscription[] = [];
  #duration: number | undefined;
  #player!: Player | undefined;

  setMute = this.effect((mute$: Observable<boolean>) => {
    return mute$.pipe(
      tap((mute) => {
        this.patchState({ mute });
        if (this.#player) {
          this.#player.setVolume(mute ? 0 : 1);
        }
      })
    );
  });

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private pageVisibilityService: PageVisibilityService,
    private effects: RxEffects,
    @Optional() @Inject(MUTE_VIMEO_VIDEO) mute$: Observable<boolean>
  ) {
    super({ mute: false });
    this.#onWaiting();
    this.effects.register(
      this.pageVisibilityService.pageVisible$,
      this.onPageVisible.bind(this)
    );

    if (mute$) {
      this.setMute(mute$);
    }
  }

  onPageVisible(pageVisible: boolean) {
    if (!pageVisible) {
      this.pause();
      this.stopped.emit(true);
    }
  }

  ngAfterViewInit(): void {
    this.isPlaying.emit(false);
    this.buffering.emit(true);
    this.currentTime.emit(0);
    this.stopped.emit(true);

    if (this.video.videoId) {
      const options: Options = {
        id: this.video.videoId,
        width: 640,
        controls: false,
        playsinline: true,
        muted: false,
        dnt: true,
      };
      if (this.vimeoContainer) {
        this.#player = new Player(this.vimeoContainer.nativeElement, options);
        this.#player.ready().then(() => {
          this.#onCanPlay();
        });
        const mute = this.get((s) => s.mute);
        this.#player.setVolume(mute ? 0 : 1);
        this.#player.on('bufferstart', () => {
          this.#onWaiting();
        });
        this.#player.on('bufferend', () => {
          this.#onCanPlay();
        });
        this.#player.on('playing', () => {
          this.#onPlaying();
        });
        this.#player.on('pause', () => {
          this.#onPause();
        });
        this.#player.on('ended', () => {
          this.stopped.emit(true);
        });
        this.#player.on('timeupdate', (event) => {
          this.#onTimeUpdate(event.seconds, event.percent);
          this.stopped.emit(false);
        });

        const duration$: Observable<number> = from(
          this.#player.getDuration()
        ) as Observable<number>;

        duration$.pipe(untilDestroyed(this)).subscribe((duration) => {
          this.#duration = duration;
          this.duration.emit(duration);
        });
      }
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.#unSubscribe();
    if (this.document.defaultView) {
      this.document.defaultView.dispatchEvent(new Event(VimeoPauseEvent));
    }
  }

  public play() {
    if (this.#player) {
      this.#player.play();
    }
  }

  public pause() {
    if (this.#player) {
      this.#player.pause();
    }
  }

  stop() {
    this.pause();
    if (this.#player) {
      this.#player.setCurrentTime(0);
    }
  }

  #onWaiting() {
    this.buffering.emit(true);
  }

  #onCanPlay() {
    this.buffering.emit(false);
  }

  #onPlaying() {
    this.isPlaying.emit(true);
    this.stopped.emit(false);
    if (this.document.defaultView) {
      this.document.defaultView.dispatchEvent(new Event(VimeoPlayEvent));
    }
  }

  #onPause() {
    this.isPlaying.emit(false);
    if (this.document.defaultView) {
      this.document.defaultView.dispatchEvent(new Event(VimeoPauseEvent));
    }
  }

  #onTimeUpdate(currentTime: number, percent: number) {
    this.currentTime.emit(currentTime);
    this.currentTime.emit();
    if (this.#duration) {
      this.currentRatio.emit(percent);
    }
    if (percent === 0) {
      this.stopped.emit(true);
    }
  }

  #unSubscribe() {
    if (this.#subscriptions) {
      this.#subscriptions.forEach((sub) => {
        if (sub) {
          sub.unsubscribe();
        }
      });
    }
  }
}

@NgModule({
  imports: [CommonModule],
  declarations: [MediaVimeoComponent],
  exports: [MediaVimeoComponent],
})
export class MediaVimeoComponentModule {}
